import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Shield, Star, Users, FileText, CreditCard } from 'lucide-react';
export function Hero() {
  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <section className="relative bg-navy-900 text-white pt-8 md:pt-16 pb-10 md:pb-20 px-4 overflow-hidden" data-id="element-99">
      {/* Decorative background pattern */}
      <div className="absolute inset-0 opacity-[0.07] pointer-events-none" style={{
      backgroundImage: 'radial-gradient(#D4A537 1px, transparent 1px)',
      backgroundSize: '32px 32px'
    }} data-id="element-100" />
      {/* Gold glow accents */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-gold/10 blur-[120px] rounded-full pointer-events-none" data-id="element-101" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/10 blur-[120px] rounded-full pointer-events-none" data-id="element-102" />

      <div className="relative max-w-3xl mx-auto flex flex-col items-center text-center" data-id="element-103">
        {/* Eyebrow badge */}
        <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="inline-flex items-center gap-2 bg-gold/10 border border-gold/30 text-gold px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-6" data-id="element-104">
          <span className="w-2 h-2 rounded-full bg-gold animate-pulse" data-id="element-105" />
          Sistema N°1 para profes en Argentina
        </motion.div>

        {/* H1 */}
        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="font-display text-5xl md:text-7xl leading-none text-white uppercase tracking-wide mb-8" data-id="element-106">
          Un año de rutinas y{' '}
          <span className="text-gold relative inline-block" data-id="element-107">
            ejercicios resueltos
            <svg className="absolute -bottom-2 left-0 w-full" height="8" viewBox="0 0 200 8" preserveAspectRatio="none" data-id="element-108">
              <path d="M0 4 Q 50 0, 100 4 T 200 4" stroke="#D4A537" strokeWidth="2" fill="none" strokeLinecap="round" data-id="element-109" />
            </svg>
          </span>
        </motion.h1>

        {/* MOCKUP at 70% — sits between H1 and subtitle */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.85
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="relative flex justify-center mb-8 w-full" data-id="element-110">
          {/* Glow behind mockup */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-gold/30 blur-[80px] rounded-full pointer-events-none" data-id="element-111" />
          {/* Decorative sparkles */}
          <motion.div animate={{
          rotate: 360
        }} transition={{
          repeat: Infinity,
          duration: 20,
          ease: 'linear'
        }} className="absolute -top-4 -left-2 md:left-8 text-gold text-2xl" data-id="element-112">
            ✦
          </motion.div>
          <motion.div animate={{
          rotate: -360
        }} transition={{
          repeat: Infinity,
          duration: 15,
          ease: 'linear'
        }} className="absolute -top-2 -right-2 md:right-8 text-gold text-xl" data-id="element-113">
            ✦
          </motion.div>

          <motion.img animate={{
          y: [0, -8, 0]
        }} transition={{
          repeat: Infinity,
          duration: 4,
          ease: 'easeInOut'
        }} src="https://cdn.magicpatterns.com/uploads/oxuGkUY2GtGwqctRiDMSgK/Mockup_megapack_fuerza_mayor-Photoroom.png" alt="Megapack Fuerza Mayor Mockup" className="w-[70%] max-w-[360px] object-contain drop-shadow-[0_25px_60px_rgba(212,165,55,0.4)] relative z-10" data-id="element-114" />
        </motion.div>

        {/* Subtitle */}
        <motion.p initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="md:text-2xl font-medium text-navy-50 mb-4 max-w-2xl text-[12px]" data-id="element-115">
          Tu año completo de entrenamiento con adultos mayores listo. 6 Tomos ·
          60+ Clases · 6 Bonos · Listo para usar.
        </motion.p>

        {/* Description */}
        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.6,
        delay: 0.5
      }} className="text-base md:text-lg text-navy-200 mb-8 max-w-2xl" data-id="element-116">
          Tu año completo de clases con adultos mayores listo: evaluación,
          fuerza, movilidad, equilibrio, clases grupales, patologías y
          comunicación. Sin improvisar.
        </motion.p>

        {/* CTA */}
        <motion.button initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.5,
        delay: 0.6
      }} whileHover={{
        scale: 1.05,
        y: -2
      }} whileTap={{
        scale: 0.97
      }} onClick={scrollToPricing} className="w-full md:w-auto bg-gold hover:bg-gold-500 text-navy-900 font-bold text-lg md:text-xl py-4 px-8 rounded-xl shadow-[0_0_30px_rgba(212,165,55,0.5)] transition-colors flex items-center justify-center gap-2 mb-6" data-id="element-117">
          <span className="text-2xl" data-id="element-118">💥</span> QUIERO MI FUERZA MAYOR AHORA
        </motion.button>

        {/* Trust badges */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.6,
        delay: 0.7
      }} className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-xs md:text-sm text-navy-200 pt-2" data-id="element-119">
          <div className="flex items-center gap-1" data-id="element-120">
            <FileText size={14} className="text-gold" data-id="element-121" /> PDF digital
          </div>
          <div className="flex items-center gap-1" data-id="element-122">
            <CheckCircle2 size={14} className="text-gold" data-id="element-123" /> Acceso inmediato
          </div>
          <div className="flex items-center gap-1" data-id="element-124">
            <Users size={14} className="text-gold" data-id="element-125" /> Todos los niveles
          </div>
          <div className="flex items-center gap-1" data-id="element-126">
            <Shield size={14} className="text-gold" data-id="element-127" /> Garantía 7 días
          </div>
          <div className="flex items-center gap-1" data-id="element-128">
            <CreditCard size={14} className="text-gold" data-id="element-129" /> Mercado Pago
          </div>
        </motion.div>

        {/* Stats row */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.8
      }} className="mt-8 pt-6 border-t border-navy-700 w-full max-w-2xl" data-id="element-130">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center" data-id="element-131">
            <div className="flex flex-col items-center" data-id="element-132">
              <div className="flex -space-x-2 mb-1" data-id="element-133">
                {[1, 2, 3, 4].map(i => <div key={i} className="w-7 h-7 rounded-full bg-navy-600 border-2 border-navy-900 flex items-center justify-center text-[10px] font-bold" data-id="element-134">
                    {String.fromCharCode(64 + i)}
                  </div>)}
              </div>
              <span className="text-xs font-bold text-navy-100" data-id="element-135">
                +500 PROFES
              </span>
            </div>
            <div className="flex flex-col items-center justify-center" data-id="element-136">
              <span className="text-2xl font-display text-gold leading-none" data-id="element-137">
                6+6
              </span>
              <span className="text-xs font-bold text-navy-100" data-id="element-138">
                TOMOS + BONOS
              </span>
            </div>
            <div className="flex flex-col items-center" data-id="element-139">
              <div className="flex items-center gap-0.5 mb-1" data-id="element-140">
                {[1, 2, 3, 4, 5].map(i => <Star key={i} size={12} className="text-gold fill-gold" data-id="element-141" />)}
              </div>
              <span className="text-xs font-bold text-navy-100" data-id="element-142">
                4.9/5 RATING
              </span>
            </div>
            <div className="flex flex-col items-center justify-center" data-id="element-143">
              <span className="text-2xl leading-none" data-id="element-144">🇦🇷</span>
              <span className="text-xs font-bold text-navy-100 mt-1" data-id="element-145">
                TODO EL PAÍS
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}